// KpiGrid satırı (M16 T10; T18/T23 ile KpiGrid.tsx'ten ayrıldı — dosya ≤600 satır kuralı).
// Dört satır türü (spec §6.1): kategori bandı (accordion), kırılım toplamı (kalın, ▾/▸, kapalıyken
// "· N kırılım"), grup bandı (StructuralRow `group` — sessiz ayırıcı), yaprak/üye (tam yol aria).
// Gösterge satırı: ad hücresi (ƒx / ⇄ işaretleri), birim,
// değer hücreleri (durum pili, amber nokta, onay noktası), değişim kolonu. T18: `evidenceCount>0`
// → ataç + sayı (tıkla → Kanıt sekmesi). T23: `isReported` → RAPORLANAN / SABİT rozeti (tıkla →
// popover). Rozetler `<button>` — hücre tıklaması editörü açmasın diye `stopPropagation`.
// Değer hücresi KpiGridValueCell.tsx'te (salt taşıma). Saf mantık lib/kpi-grid-nav.ts'te.
// TODO-KPI-07: her `<tr>` sabit `gridRowHeight` (36 px; portal 56 px) — sanallaştırma doldurucuları bu
// yükseklikten hesaplanır (bant satırı dahil). TODO-KPI-06: ad kolonu genişliği `nameColWidth`
// (≤1024 px'te 220) — hücre sticky-left, yatay kaydırmada sabit.

import { indicatorFootnoteKey, type KpiFormMismatch } from "@mahir/shared";
import { KpiFootnoteMark } from "./KpiFootnoteList";
import {
  BandStats,
  ClientStatusCell,
  StructuralRow,
  TD_BORDER,
  TotalName,
} from "./KpiGridRowParts";
import { KpiGridValueCell } from "./KpiGridValueCell";
import type { CellPos, GridColumn, GridRow } from "../../lib/kpi-grid-nav";
import {
  changeForIndicator,
  gridRowHeight,
  isTotalColumn,
  rowIndent,
} from "../../lib/kpi-grid-nav";
import { KPI_BREAKDOWN_COPY } from "../../lib/kpi-breakdown-copy";
import { type GridNotes } from "../../lib/kpi-notes-ui";
import type { PartialLookup } from "../../lib/kpi-partial-info";
import { KPI_COPY } from "../../lib/kpi-ui";
import type { PortalUnitChanged } from "../../lib/kpi-unit-changed";

/** T19 — portal (müşteri) görünümü: 🔒 readonly satır, `editable` hücreler, "yıl durumu" kolonu. */
export interface GridClientView {
  setId: string;
  /** P1-1 — konsolide (salt okunur) görünüm: durum kolonu çizilmez (toplamın onay durumu yok) */
  consolidated?: boolean | undefined;
  /** P3-15 — "Satıra git" hedef yılı: o yılın hücresine odak/scroll */
  highlightYear?: number | null | undefined;
  /** P2-11 — durum kolonu sağa yapışık yalnız ≥1024 px (KpiGrid matchMedia ile verir) */
  stickyStatus?: boolean | undefined;
  /** P2-11 — ≤640 px'te ad kolonu dar (320 → 170) ki değer hücreleri görünür kalsın */
  nameColWidth?: number | undefined;
  /** §12/1 — "Yıllar" görünümünde yıl başına salt-okunur toplam sütun grubu (KpiGrid → buildColumns) */
  totalColumns?: boolean | undefined;
  /** DR9/DR13 — 409 KPI_UNIT_CHANGED hücreleri: kapalı hücrede amber nokta, editörde amber satır */
  unitChanged?: Record<string, PortalUnitChanged> | undefined;
}

export interface KpiGridRowProps {
  row: GridRow;
  rowIndex: number;
  columns: GridColumn[];
  showChangeCol: boolean;
  focus: CellPos | null;
  flash: boolean;
  readOnly: boolean;
  saveErrors: Record<string, string>;
  cellRefs: React.MutableRefObject<Map<string, HTMLTableCellElement>>;
  onToggleBand: (id: string) => void;
  onFocusCell: (pos: CellPos) => void;
  onActivate: (pos: CellPos) => void;
  /** ataç rozeti: hücre editörünü Kanıt sekmesinde açar (T18) */
  onOpenEvidence: (pos: CellPos) => void;
  /** RAPORLANAN / SABİT rozeti: popover (T23) */
  onOpenReported: (pos: CellPos) => void;
  /** T21 — (metricId, year) için form yanıtı ↔ KPI uyuşmazlığı; rozet "form yanıtını al" */
  formMismatchFor?: ((metricId: string, year: number) => KpiFormMismatch | undefined) | undefined;
  onApplyFormValue?: ((m: KpiFormMismatch) => void) | undefined;
  clientView?: GridClientView | undefined;
  /** Dalga 3 — hücre notu özeti (rozet) + thread açma; yoksa rozet çizilmez */
  notes?: GridNotes | undefined;
  onOpenNotes?: ((pos: CellPos) => void) | undefined;
  /** ad kolonu genişliği (KpiGrid matchMedia ile verir; clientView.nameColWidth'ten öncelikli) */
  nameColWidth?: number | undefined;
  /** K8 — kısmi toplam ⓘ penceresi: kod → etiket, işletme → ad; yoksa ⓘ çizilmez */
  partialLookup?: PartialLookup | undefined;
  /** DR4 bekleme — toplam hücresi (gösterge × kolon) üye kaydı sürerken meşgul mü */
  isRecomputing?: ((indicatorId: string, col: GridColumn) => boolean) | undefined;
  /** K9 — ≤768 px: girinti adımı 18 → 12, kapalı toplam eki yalnız sayı */
  narrow?: boolean | undefined;
}

export function KpiGridRow(p: KpiGridRowProps): JSX.Element {
  const { row, rowIndex, columns, showChangeCol, flash, onToggleBand, clientView } = p;
  const ind = row.indicator;
  const rowReadonly = !!clientView && ind.effectiveClientAccess !== "open";
  const extraCols = (showChangeCol ? 1 : 0) + (clientView ? 1 : 0);
  const flashBg = "color-mix(in srgb, var(--color-primary) 12%, white)";
  const rowBg = flash
    ? flashBg
    : row.isBand || (!!clientView && ind.effectiveClientAccess !== "open" && !row.isBand)
      ? "var(--color-bg)"
      : "transparent";
  const firstTd: React.CSSProperties = {
    position: "sticky",
    left: 0,
    background: flash ? flashBg : row.isBand ? "var(--color-bg)" : "var(--color-surface)",
    padding: "7px 10px",
    paddingLeft: rowIndent(row.depth, !!p.narrow),
    borderBottom: TD_BORDER,
    fontSize: "0.85rem",
    color: "var(--color-text)",
    zIndex: 1,
    maxWidth: p.nameColWidth ?? clientView?.nameColWidth ?? 320,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };

  if (row.indicator.structural) {
    // Y4a — değersiz iskelet bant (hücre `<td>`, sayaç, not rozeti yok)
    return (
      <StructuralRow
        row={row}
        rowIndex={rowIndex}
        colSpan={2 + columns.length + extraCols}
        onToggleBand={onToggleBand}
        clientView={!!clientView}
        flash={flash}
        variant="category"
        narrow={p.narrow}
      />
    );
  }

  if (row.kind === "group") {
    // kırılım grubu bandı — sessiz ayırıcı (DR1): hücresiz, sayısız, aç/kapanmaz
    return (
      <StructuralRow
        row={row}
        rowIndex={rowIndex}
        colSpan={2 + columns.length + extraCols}
        onToggleBand={onToggleBand}
        clientView={!!clientView}
        flash={flash}
        variant="group"
        narrow={p.narrow}
      />
    );
  }

  if (row.isBand) {
    const open = row.open !== false;
    const s = row.bandStats;
    return (
      <tr
        aria-rowindex={rowIndex + 1}
        {...row.tree}
        style={{ background: rowBg, height: gridRowHeight(!!clientView) }}
      >
        <td style={{ ...firstTd, fontWeight: 700, color: "var(--color-primary)" }} colSpan={1}>
          <button
            type="button"
            aria-expanded={open}
            onClick={() => onToggleBand(ind.id)}
            style={{ color: "inherit", fontWeight: 700, minHeight: 28 }}
          >
            <span aria-hidden="true" style={{ marginRight: 6 }}>
              {open ? "▾" : "▸"}
            </span>
            {ind.label}
            <KpiFootnoteMark refKey={indicatorFootnoteKey(ind.id)} />
          </button>
          {clientView && rowReadonly ? (
            <span
              className="ml-2 text-xs"
              style={{ color: "var(--color-text-3)", fontWeight: 400 }}
              title={KPI_COPY.portal.lockTitle}
            >
              yalnız görüntüleme <span aria-hidden="true">🔒</span>
            </span>
          ) : null}
        </td>
        <td
          colSpan={columns.length + 1 + extraCols}
          style={{
            borderBottom: TD_BORDER,
            background: rowBg,
            // portal (P3-13) + danışman (P3-15): özet sabit genişlikli ad hücresinde "…" ile
            // kesilmesin — kalan kolonlara yayılan hücrede, kısaltmasız
            padding: "7px 10px",
            whiteSpace: "nowrap",
          }}
        >
          {s ? <BandStats s={s} /> : null}
        </td>
      </tr>
    );
  }

  const firstTotalIndex = columns.findIndex(isTotalColumn);
  const nameAria =
    row.member?.path ??
    (row.kind === "total" && row.total
      ? clientView
        ? KPI_BREAKDOWN_COPY.portal.totalAria(ind.label, row.total.memberCount)
        : KPI_BREAKDOWN_COPY.totalAria(
            ind.label,
            row.total.groups.map((g) => g.label),
          )
      : undefined);
  const change = showChangeCol && ind.showChange ? changeForIndicator(ind, columns) : null;
  const tone =
    change && change.pct !== null
      ? ind.increaseIsGood === null || change.pct === 0
        ? "neutral"
        : change.pct > 0 === ind.increaseIsGood
          ? "good"
          : "bad"
      : "neutral";

  return (
    <tr
      aria-rowindex={rowIndex + 1}
      {...row.tree}
      style={{ background: rowBg, height: gridRowHeight(!!clientView) }}
    >
      <td
        style={firstTd}
        title={row.member?.path ?? ind.label}
        // DR9 — üye: tam yol ("Toplam › Cinsiyete göre › Kadın"); toplam: grup listesi
        {...(nameAria ? { "aria-label": nameAria } : {})}
      >
        {/* Final review minör 1 — etiket kendi kutusunda kırpılır; dipnot/ƒx/⇄/🔒 görünür kalır */}
        <div className="flex items-baseline">
          {row.kind === "total" && row.total ? (
            <TotalName
              row={row}
              clientView={!!clientView}
              narrow={!!p.narrow}
              onToggle={() => onToggleBand(ind.id)}
            />
          ) : (
            <span className="truncate min-w-0">{row.member?.label ?? ind.label}</span>
          )}
          <KpiFootnoteMark refKey={indicatorFootnoteKey(ind.id)} />
          {ind.isDerived ? (
            <span
              className="ml-1 text-xs font-semibold"
              style={{ color: "var(--color-primary)" }}
              title="Hesaplanan gösterge"
            >
              ƒx
            </span>
          ) : null}
          {ind.linkedQuestionCode && !clientView ? (
            <span
              className="ml-1 text-xs"
              style={{ color: "var(--color-text-3)" }}
              title={`Form sorusu: ${ind.linkedQuestionCode}`}
            >
              ⇄
            </span>
          ) : null}
          {clientView && rowReadonly ? (
            <span
              className="ml-1 text-xs"
              role="img"
              aria-label={KPI_COPY.portal.lockTitle}
              title={KPI_COPY.portal.lockTitle}
            >
              🔒
            </span>
          ) : null}
        </div>
      </td>
      <td
        style={{
          padding: "7px 10px",
          borderBottom: TD_BORDER,
          fontSize: "0.78rem",
          color: "var(--color-text-3)",
          whiteSpace: "nowrap",
        }}
      >
        {ind.valueType === "percent" ? "%" : (ind.unit ?? "")}
      </td>
      {columns.map((col, ci) => (
        <KpiGridValueCell
          key={col.key}
          p={p}
          col={col}
          ci={ci}
          rowReadonly={rowReadonly}
          firstTotalIndex={firstTotalIndex}
        />
      ))}
      {showChangeCol ? (
        <td
          style={{
            padding: "6px 10px",
            borderBottom: TD_BORDER,
            textAlign: "right",
            fontSize: "0.8rem",
            whiteSpace: "nowrap",
            color:
              tone === "good"
                ? "var(--color-success)"
                : tone === "bad"
                  ? "var(--color-error)"
                  : "var(--color-text-3)",
          }}
        >
          {change && change.pct !== null
            ? `${change.pct > 0 ? "↑" : change.pct < 0 ? "↓" : "→"} %${Math.abs(change.pct).toLocaleString("tr-TR", { maximumFractionDigits: 1 })}`
            : ""}
        </td>
      ) : null}
      {clientView && !clientView.consolidated ? (
        <ClientStatusCell row={row} columns={columns} sticky={clientView.stickyStatus !== false} />
      ) : null}
    </tr>
  );
}
